import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND}/api/admin/editor`;
const TOKEN_KEY = 'site_editor_token';
// كوكي بتحطه لوحة التحكم لما الأدمن يدخل — معناه بس "جرّبي تطلبي توكن تعديل"
const HINT_COOKIE = 'site_editor';

const readToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const writeToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* التخزين غير متاح */
  }
};

const hasHintCookie = () => document.cookie.split('; ').some((c) => c.startsWith(`${HINT_COOKIE}=`));

const SiteEditorContext = createContext({ admin: null, token: null });

/**
 * وضع "تعديل الصفحات من الموقع" للأدمن (زي ووردبريس).
 *  1) ?edit_code= جاي من زر "تعديل على الموقع" في لوحة التحكم → نبدّله بتوكن.
 *  2) توكن محفوظ من قبل → نتأكد إنه لسه صالح.
 *  3) نفس الدومين والأدمن داخل على لوحة التحكم → نطلب توكن بالـ session.
 */
export const SiteEditorProvider = ({ children }) => {
  const [token, setToken] = useState(readToken);
  const [admin, setAdmin] = useState(null);

  const accept = useCallback((data) => {
    writeToken(data.token);
    setToken(data.token);
    setAdmin(data.admin);
  }, []);

  const clear = useCallback(() => {
    writeToken(null);
    setToken(null);
    setAdmin(null);
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('edit_code');

    if (code) {
      url.searchParams.delete('edit_code');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      axios
        .post(`${API}/exchange`, { code })
        .then((res) => accept(res.data.data))
        .catch(() => clear());
      return;
    }

    const saved = readToken();
    if (saved) {
      axios
        .get(`${API}/me`, { headers: { Authorization: `Bearer ${saved}` } })
        .then((res) => setAdmin(res.data.data.admin))
        .catch((err) => {
          // 401/403 بس يعني التوكن خلص — غير كده (شبكة) نسيبه
          if ([401, 403].includes(err.response?.status)) clear();
        });
      return;
    }

    if (hasHintCookie()) {
      axios
        .get(`${BACKEND}/admin/site-editor/session`, {
          withCredentials: true,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        })
        .then((res) => accept(res.data.data))
        .catch((err) => {
          // مش داخل أو ملوش صلاحية الصفحات: منسألش تاني لحد ما يدخل من جديد
          if ([401, 403].includes(err.response?.status)) document.cookie = `${HINT_COOKIE}=; Max-Age=0; path=/`;
        });
    }
  }, [accept, clear]);

  const exit = useCallback(() => {
    if (token) {
      axios.post(`${API}/logout`, null, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
    clear();
  }, [token, clear]);

  const savePage = useCallback(
    (slug, page) =>
      axios
        .put(`${API}/pages/${slug}`, page, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => res.data.data)
        .catch((err) => {
          if (err.response?.status === 401) clear();
          throw err;
        }),
    [token, clear]
  );

  const value = useMemo(
    () => ({ admin, canEdit: Boolean(admin && token), savePage, exit, panelUrl: `${BACKEND}/admin/pages` }),
    [admin, token, savePage, exit]
  );

  return <SiteEditorContext.Provider value={value}>{children}</SiteEditorContext.Provider>;
};

export const useSiteEditor = () => useContext(SiteEditorContext);
